import algoliaSearch, { type SearchClient } from 'algoliasearch/lite';
import type { MultipleQueriesOptions, MultipleQueriesQuery, MultipleQueriesResponse } from '@algolia/client-search';
import type { RequestOptions } from '@algolia/transporter';
import type { AxiosInstance } from 'axios';

import { getLocale } from '@monolith/legacy/services/i18n';
import { isEnabled } from '@monolith/legacy/services/features';

import { sendRequestToAnkorsearch, sendFiltersRequestToAnkorsearch } from './ankorsearch-request';
import type { AnkorsearchFiltersResponse } from './ankorsearch-request';
import { Context } from '../../types/catalog';

function mergeSearchResponses<TResult>(
  productsResponse: MultipleQueriesResponse<TResult>,
  filtersResponse: { results: Array<{ facets?: Record<string, Record<string, number>>; facets_stats?: Record<string, unknown> }> }
): MultipleQueriesResponse<TResult> {
  return {
    results: productsResponse.results.map((result, i) => ({
      ...result,
      facets: filtersResponse.results[i]?.facets ?? {},
      facets_stats: filtersResponse.results[i]?.facets_stats,
    })) as MultipleQueriesResponse<TResult>['results'],
  };
}

function isMainSearchBatch(requests: Readonly<MultipleQueriesQuery[]>): boolean {
  return requests.some((req) => {
    if (typeof req.params === 'string') {
      return new URLSearchParams(req.params).get('hitsPerPage') === '100';
    }
    return (req.params as { hitsPerPage?: number } | undefined)?.hitsPerPage === 100;
  });
}

const FILTERS_FINGERPRINT_KEYS = ['query', 'filters', 'facetFilters', 'numericFilters', 'tagFilters', 'optionalFilters'] as const;

function computeFiltersFingerprint(requests: Readonly<MultipleQueriesQuery[]>): string {
  return requests
    .map((req) => {
      const params: Record<string, unknown> =
        typeof req.params === 'string'
          ? Object.fromEntries(new URLSearchParams(req.params).entries())
          : ((req.params as Record<string, unknown> | undefined) ?? {});
      const subset = FILTERS_FINGERPRINT_KEYS.reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = params[key] ?? '';
        return acc;
      }, {});
      return `${req.indexName}:${JSON.stringify(subset)}`;
    })
    .join('|');
}

type RequestTransformer = (
  request: Readonly<MultipleQueriesQuery[]>
) => Readonly<MultipleQueriesQuery[]> | Promise<Readonly<MultipleQueriesQuery[]>>;

type ResponseTransformer = <TResult>(
  response: Readonly<MultipleQueriesResponse<TResult>>
) => Readonly<MultipleQueriesResponse<TResult>> | Promise<Readonly<MultipleQueriesResponse<TResult>>>;

function getSearchHostUrl(host: string): string {
  return `${host}/api/v2`;
}

const ANKORSEARCH_ENABLED_CONTEXTS: Context[] = [
  Context.SEARCH,
];

export class SearchApiClient implements SearchClient {
  // singleton instance of the Algolia SearchClient to be used across the application.
  // This ensures that the client is only initialized once and the cache is shared.
  private static client: SearchClient;
  private static currentHostConfig: string;
  private static activeContext?: Context;
  private static http: AxiosInstance;
  private static lastFiltersFingerprint: string | null = null;
  private static lastFiltersPromise: Promise<AnkorsearchFiltersResponse> | null = null;
  // True while a /queries call is being issued from the FF-ON + main-batch path,
  // signaling the requester to ask Ankorsearch to skip facet aggregations.
  private static skipQueriesAggregations = false;
  // Set by the QR-referral /lift catalog: the active campaign UUID forwarded to the
  // monolith proxy as X-Campaign-Id so it can reveal that brand's wholesale prices.
  private static campaignId?: string;

  public static setCampaignId(campaignId?: string): void {
    SearchApiClient.campaignId = campaignId || undefined;
  }

  private static isAnkorsearchContext(): boolean {
    return !!SearchApiClient.activeContext && ANKORSEARCH_ENABLED_CONTEXTS.includes(SearchApiClient.activeContext);
  }

  private static init(http: AxiosInstance, host: string) {
    SearchApiClient.http = http;
    const hostUrl = getSearchHostUrl(host);

    // Reinitialize if the host config has changed
    if (!SearchApiClient.client || SearchApiClient.currentHostConfig !== hostUrl) {
      SearchApiClient.currentHostConfig = hostUrl;
      SearchApiClient.client = algoliaSearch('ankorstore', 'ankorstore', {
        hosts: [
          {
            url: hostUrl,
            protocol: 'https',
          },
        ],
        timeouts: {
          connect: 10,
          read: 10,
          write: 30,
        },
        requester: {
          send: async (request) => {
            try {
              if (SearchApiClient.isAnkorsearchContext()) {
                return await sendRequestToAnkorsearch(
                  http,
                  request,
                  getLocale(),
                  SearchApiClient.skipQueriesAggregations ? { withAggregations: false } : undefined
                );
              }

              const response = await http.request({
                ...request,
                headers: {
                  ...request.headers,
                  // Forward the QR campaign so the monolith can reveal that brand's
                  // wholesale prices to this logged-out /lift visitor. The server
                  // verifies it (brand-scoped, non-deleted), so a stale id is harmless.
                  ...(SearchApiClient.campaignId ? { 'X-Campaign-Id': SearchApiClient.campaignId } : {}),
                },
                // Prevent axios from parsing the response as JSON.
                // We want to keep it as a string because the Algolia client will parse it.
                transformResponse: [],
              });

              return {
                content: response.data,
                isTimedOut: false,
                status: response.status,
              };
            } catch (error) {
              return {
                content: error.response?.data,
                isTimedOut: error.code === 'ECONNABORTED',
                status: error.response?.status,
              };
            }
          },
        },
      });
    }
  }

  private readonly requestTransformer: RequestTransformer[] = [];
  private readonly responseTransformer: ResponseTransformer[] = [];
  private readonly context?: Context;

  public constructor(http: AxiosInstance, host: string, context?: Context) {
    this.context = context;
    SearchApiClient.init(http, host);
  }

  public get searchForFacetValues() {
    // Set active context so the custom requester routes to ankorsearch
    SearchApiClient.activeContext = this.context;

    return SearchApiClient.client.searchForFacetValues;
  }

  public get initIndex() {
    return SearchApiClient.client.initIndex;
  }

  public get addAlgoliaAgent() {
    return SearchApiClient.client.addAlgoliaAgent;
  }

  public get appId() {
    return SearchApiClient.client.appId;
  }

  public get transporter() {
    return SearchApiClient.client.transporter;
  }

  public get clearCache() {
    return SearchApiClient.client.clearCache;
  }

  public get customRequest() {
    return SearchApiClient.client.customRequest;
  }

  public get getRecommendations() {
    return SearchApiClient.client.getRecommendations;
  }

  public async search<TResult>(requests: Readonly<MultipleQueriesQuery[]>, options?: RequestOptions & MultipleQueriesOptions) {
    const transformedRequests = await this.requestTransformer.reduce(
      async (acc, transformer) => transformer(await acc),
      Promise.resolve(requests)
    );

    SearchApiClient.activeContext = this.context;
    SearchApiClient.skipQueriesAggregations = false;

    if (
      SearchApiClient.isAnkorsearchContext() &&
      isEnabled('DAT-3137') &&
      isMainSearchBatch(transformedRequests)
    ) {
      SearchApiClient.skipQueriesAggregations = true;
      const fingerprint = computeFiltersFingerprint(transformedRequests);

      if (fingerprint !== SearchApiClient.lastFiltersFingerprint || !SearchApiClient.lastFiltersPromise) {
        const requestBody = {
          requests: transformedRequests.map((req) => ({ indexName: req.indexName, params: req.params ?? '' })),
        };
        SearchApiClient.lastFiltersFingerprint = fingerprint;
        SearchApiClient.lastFiltersPromise = sendFiltersRequestToAnkorsearch(
          SearchApiClient.http,
          requestBody,
          getLocale()
        ).catch((error) => {
          if (SearchApiClient.lastFiltersFingerprint === fingerprint) {
            SearchApiClient.lastFiltersFingerprint = null;
            SearchApiClient.lastFiltersPromise = null;
          }
          throw error;
        });
      }

      const [productsResponse, filtersResponse] = await Promise.all([
        SearchApiClient.client.search<TResult>(transformedRequests, options),
        SearchApiClient.lastFiltersPromise,
      ]);

      const merged = mergeSearchResponses(productsResponse, filtersResponse);

      return this.responseTransformer.reduce(
        async (acc, transformer) => transformer(await acc),
        Promise.resolve(merged)
      );
    }

    const response = await SearchApiClient.client.search<TResult>(transformedRequests, options);

    const transformedResponse = await this.responseTransformer.reduce(
      async (acc, transformer) => transformer(await acc),
      Promise.resolve(response)
    );

    return transformedResponse;
  }

  public onRequest(transformer: RequestTransformer) {
    this.requestTransformer.push(transformer);

    return this;
  }

  public onResponse(transformer: ResponseTransformer) {
    this.responseTransformer.push(transformer);

    return this;
  }

  public destroy() {
    this.requestTransformer.length = 0;
    this.responseTransformer.length = 0;
  }
}
